import mixpanel from "mixpanel-browser";
import { useContext, useCallback, useMemo } from "react";
import { LanguageContext } from "./LanguageContext";

interface MixPanelEvent {
  [key: string]: unknown;
  language?: string;
}

interface LanguageContextType {
  currentLanguage: string;
}

/**
 * Returns a memoized `logEvent` function for tracking Mixpanel events. The
 * user identity + people-property side-effect that used to live in this hook
 * has been lifted to `<MixPanelIdentitySync />`, which mounts once at the top
 * of the provider tree — see that file for the rationale (GO-2864).
 *
 * `mixpanel.init()` happens once at app bootstrap in `src/index.tsx`.
 */
export const useMixPanel = () => {
  const { currentLanguage } = useContext(
    LanguageContext
  ) as unknown as LanguageContextType;

  const logEvent = useCallback(
    (eventName: string, eventDetail: MixPanelEvent = {}) => {
      mixpanel.track(eventName, { ...eventDetail, language: currentLanguage });
    },
    [currentLanguage]
  );

  return useMemo(() => ({ logEvent }), [logEvent]);
};
